import { RichText } from "@atproto/api";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogPortal,
} from "@radix-ui/react-dialog";
import {
  DotsVerticalIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
} from "@radix-ui/react-icons";
import {
  useInfiniteQuery,
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import Hls from "hls.js";
import {
  HeartIcon,
  MessageCircleIcon,
  Repeat2Icon,
  ReplyIcon,
  XIcon,
} from "lucide-react";
import Masonry from "masonry-layout";
import * as React from "react";
import { useEffect } from "react";
import { useEditor } from "tldraw";
import { useSize } from "use-shared-resize-observer/use-size";

import { CanvasEditorMachine } from "@/components/canvas-editor/canvas-editor-machine";
import {
  CopyLinkMenuItem,
  CopyUserDidMenuItem,
} from "@/components/CopyUserDidMenuItem";
import { useToast } from "@/components/hooks/use-toast";
import { useEvent } from "@/components/hooks/useEvent";
import { LoadMoreSpinner } from "@/components/LoadMoreSpinner";
import { PdsLsMenuItem } from "@/components/PdsLsMenuItem";
import { Spinner } from "@/components/Spinner";
import { StickyPostMenuItems } from "@/components/StickyPostMenuItems";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  MenuContent,
  MenuTrigger,
  MenuItem,
  MenuSeparator,
} from "@/components/ui/menu";
import {
  PopoverTrigger,
  Popover,
  PopoverContent,
} from "@/components/ui/popover";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Anchor } from "@/components/ui/typography";
import {
  feedQueryOptions,
  getCurrentFeedOptions,
  hidePostForAllFeeds,
} from "@/lib/api";
import { insertPosts, removePosts, restorePost } from "@/lib/feedWrites";
import { isSensitiveByLabel } from "@/lib/moderation";
import { FeedVersion, Post } from "@/lib/types";
import { useFeedAccessById } from "@/lib/useFeedAccess";
import { apiErrorMessage, atUriToBskyUrl, cn, isVideoFeed } from "@/lib/utils";

interface ImageBlob {
  $type: "blob";
  size: number;
  mimeType: string;
  ref: { $link?: string; link?: string };
}

interface ImageEmbed {
  $type: "app.bsky.embed.images";
  images: Array<{
    alt: string;
    image: ImageBlob;
    thumb: string;
  }>;
}

function isImageEmbed(embed: Embed): embed is ImageEmbed {
  return embed.$type.includes("app.bsky.embed.images");
}

// A gallery is the newer image embed: items live under `items` (up to 20) rather than
// `images` (up to 4), and the view calls the small variant `thumbnail`, not `thumb`.
interface GalleryEmbed {
  $type: "app.bsky.embed.gallery";
  items: Array<{
    alt: string;
    aspectRatio?: { width: number; height: number };
    // View shape; the record shape carries `image` instead.
    thumbnail?: string;
    fullsize?: string;
    image?: ImageBlob;
  }>;
}

function isGalleryEmbed(embed: Embed): embed is GalleryEmbed {
  return embed.$type.includes("app.bsky.embed.gallery");
}

/** Normalize gallery items onto the shape PostImages already renders. */
function galleryImages(
  did: string,
  embed: GalleryEmbed,
): ImageEmbed["images"] {
  return embed.items.map((item) => ({
    alt: item.alt,
    image: item.image as ImageBlob,
    thumb: item.thumbnail ?? (item.image ? makeImageUrl(did, item.image) : ""),
  }));
}

interface VideoEmbed {
  $type: "app.bsky.embed.video";
  mimeType: string;
  size: number;
  ref: { $link: string };
  alt?: string;
  playlist: string;
  thumbnail: string;
  aspectRatio?: {
    width: number;
    height: number;
  };
}

function isVideoEmbed(embed: Embed): embed is VideoEmbed {
  return embed.$type.includes("app.bsky.embed.video");
}

interface ExternalEmbed {
  $type: "app.bsky.embed.external";
  external: {
    title?: string;
    description: string;
    uri: string;
    thumb?: string;
  };
}

function isExternalEmbed(embed: Embed): embed is ExternalEmbed {
  return embed.$type.includes("app.bsky.embed.external");
}

type BasicEmbed = ImageEmbed | GalleryEmbed | VideoEmbed | ExternalEmbed;

interface MediaEmbed {
  $type: "app.bsky.embed.recordWithMedia";
  media: BasicEmbed;
}

function isMediaEmbed(embed: Embed): embed is MediaEmbed {
  return embed.$type.includes("app.bsky.embed.recordWithMedia");
}

interface StrongRef {
  $type: "com.atproto.repo.strongRef";
}

interface RecordEmbedPost {
  $type: "app.bsky.embed.record";
  record: Post & {
    type: "app.bsky.embed.record#viewRecord";
  };
}

interface RecordEmbedStrongRef {
  $type: "app.bsky.embed.record";
  record: StrongRef;
}

interface RecordEmbedFeedGen {
  $type: "app.bsky.embed.record";
  record: {
    $type: "app.bsky.feed.defs#generatorView";
    avatar: string;
    description: string;
    displayName: string;
    uri: string;
    creator: {
      handle: string;
    };
  };
}

type RecordEmbed = RecordEmbedPost | RecordEmbedStrongRef | RecordEmbedFeedGen;

function isStrongRefEmbed(embed: RecordEmbed): embed is RecordEmbedStrongRef {
  return (
    embed.$type.includes("com.atproto.repo.record") &&
    embed.record.$type === "com.atproto.repo.strongRef"
  );
}

function isRecordEmbed(embed: RecordEmbed): embed is RecordEmbedPost {
  return (
    embed.$type.includes("app.bsky.embed.record") &&
    embed.record.$type === "com.atproto.repo.strongRef"
  );
}

function isFeedGenEmbed(embed: RecordEmbed): embed is RecordEmbedFeedGen {
  return (
    embed.$type.includes("app.bsky.embed.record") &&
    embed.record.$type === "app.bsky.feed.defs#generatorView"
  );
}

type Embed = BasicEmbed | MediaEmbed | RecordEmbed;

function makeImageUrl(did: string, image: ImageBlob | string) {
  if (typeof image === "string") {
    return image;
  }

  return `https://cdn.bsky.app/img/feed_thumbnail/plain/${did}/${
    image.ref.$link || image.ref.link
  }@${image.mimeType.replace("image/", "")}`;
}

function AltBadge({ alt, className }: { alt: string; className?: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "text-[8px] bg-slate-4 dark:bg-slatedark-4 p-1 rounded-md text-slate-dim m-1",
            className,
          )}
        >
          ALT
          <span className="sr-only">: show alt text</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="text-xs" side="top">
        {alt}
      </PopoverContent>
    </Popover>
  );
}

function SensitiveOverlay({
  variant,
  feedVariant,
  actions,
}: {
  variant?: "video" | "image";
  feedVariant?: "video" | "post";
  actions?: React.ReactNode;
}) {
  const [visible, setVisible] = React.useState(false);
  const hideSensitiveContent = React.use(HideSensitiveContentContext);

  if (!hideSensitiveContent) {
    return null;
  }

  return (
    <>
      {!visible && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className={cn(
            "absolute z-100 inset-0 overflow-hidden bg-slatea-10 dark:bg-slatedarka-10 backdrop-blur-lg flex justify-center items-center",
            feedVariant !== "video" && "rounded-lg",
            "flex gap-1",
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant="action"
            size="sm"
            onClick={() => {
              setVisible(true);
            }}
          >
            Show
          </Button>
          {actions}
        </div>
      )}
      {visible && (
        <div
          className={cn(
            "absolute rounded-lg inset-0 overflow-hidden flex justify-center opacity-0 hover:opacity-100 transition-opacity",
            variant === "video" && "items-start pt-4",
            variant === "image" && "items-center",
          )}
        >
          <Button
            variant="action"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setVisible(false);
            }}
          >
            Hide
          </Button>
        </div>
      )}
    </>
  );
}

function PostImages({
  images,
  isSensitive,
}: {
  images: ImageEmbed["images"];
  isSensitive: boolean;
}) {
  const [lightbox, setLightbox] = React.useState(0);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const imageStyle = React.use(ImageStyleContext);

  const firstImage = images[lightbox];

  return (
    <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
      <div
        className={cn(
          imageStyle ? "" : "gap-2",
          images.length > 1 ? "flex overflow-auto" : undefined,
          images.length > 1 && !imageStyle && "gap-2",
        )}
      >
        {images.map((image, index) => {
          return (
            <button
              type="button"
              key={image.thumb}
              className={cn(
                "relative overflow-hidden bg-slate-subtle flex items-center justify-center w-fit",
                images.length === 1 && imageStyle && "w-full",
              )}
              onClick={() => {
                setLightbox(index);
                setLightboxOpen(true);
              }}
            >
              <img
                src={image.thumb}
                alt={image.alt}
                className={cn("flex-[0.5]", !imageStyle && "max-h-[300px]")}
              />

              {image.alt && (
                <AltBadge
                  alt={image.alt}
                  className="absolute bottom-0 right-0"
                />
              )}

              {isSensitive && (
                <SensitiveOverlay variant="image" feedVariant="post" />
              )}
            </button>
          );
        })}
      </div>

      <DialogPortal>
        <DialogContent
          className="fixed inset-0 z-10000 bg-slatea-12"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="flex flex-col gap-4 h-full py-4">
            {firstImage && (
              <div className="flex flex-col gap-2 items-center justify-center px-4 flex-1 min-h-0">
                <div className="object-contain flex-1 min-h-0 flex items-center justify-center">
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
                  <img
                    src={firstImage.thumb}
                    alt={firstImage.alt}
                    className="h-auto w-full object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {firstImage.alt && (
                  <div className="text-slate-3 text-sm italic">
                    {firstImage.alt}{" "}
                  </div>
                )}
              </div>
            )}

            {images.length > 1 && (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                className="flex gap-2 h-[80px] justify-center items-center"
                onClick={(e) => e.stopPropagation()}
              >
                {images.map((image, index) => {
                  return (
                    <button
                      type="button"
                      key={image.thumb}
                      className="h-full rounded-lg overflow-hidden bg-slate-subtle flex items-center justify-center border border-slatedark-1 w-fit"
                      onClick={() => {
                        setLightbox(index);
                      }}
                    >
                      <img
                        src={image.thumb}
                        alt={image.alt}
                        className="w-full h-full object-contain"
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <DialogClose asChild>
            <button
              className="absolute top-4 right-4 h-8 w-8 hover:bg-slatedark-5 rounded text-slatedark-12 flex items-center justify-center"
              type="button"
            >
              <span className="sr-only">Close</span>
              <XIcon />
            </button>
          </DialogClose>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

const VideoPlayer = ({
  playlist,
  alt,
  thumbnail,
  style,
  className,
  aspectRatio,
  onPlayingChange: onPlayingChangeProp,
  isSensitive,
  variant = "video",
  actions,
}: Pick<VideoEmbed, "playlist" | "alt" | "thumbnail" | "aspectRatio"> & {
  style?: React.CSSProperties;
  onPlayingChange?: (isPlaying: boolean) => void;
  className?: string;
  isSensitive: boolean;
  variant?: "video" | "post";
  actions?: React.ReactNode;
}) => {
  const masonry = React.use(MasonryContext);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const onPlayingChange = useEvent((playing: boolean) => {
    setIsPlaying(playing);
    onPlayingChangeProp?.(playing);
  });

  React.useEffect(() => {
    if (isPlaying) {
      const el = document.querySelector<HTMLElement>("#preview-feed");

      if (!el) {
        return;
      }

      function onScroll() {
        if (videoRef.current) {
          videoRef.current.pause();
          onPlayingChange(false);
        }
      }

      el.addEventListener("scroll", onScroll);
      return () => {
        el.removeEventListener("scroll", onScroll);
      };
    }
  }, [isPlaying, onPlayingChange]);

  React.useEffect(() => {
    const hls = new Hls({
      capLevelToPlayerSize: true,
      xhrSetup(xhr, urlString) {
        const url = new URL(urlString);
        url.searchParams.delete("session_id");
        xhr.open("get", url.toString());
      },
    });

    // Redirect .{m3u8,ts} files directly to the CDN, skipping the watch time/retention tracking
    //
    // Worth noting, I don't think `session_id` is tied to your account in any way, this is
    // mostly an effort to get videos to load faster since this player is lazily-loaded
    hls.loadSource(
      playlist.replace(
        "https://video.bsky.app/watch/",
        "https://video.cdn.bsky.app/hls/",
      ),
    );

    if (videoRef.current) {
      hls.attachMedia(videoRef.current);
    }
    return () => {
      hls.destroy();
    };
  }, [playlist]);

  const togglePlaying = () => {
    if (isPlaying) {
      videoRef.current?.pause();
      onPlayingChange(false);
    } else {
      videoRef.current
        ?.play()
        .then(() => onPlayingChange(true))
        .catch(console.error);
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={cn("h-full w-full", className)}
      style={style}
      onClick={togglePlaying}
      onMouseEnter={() => {
        if (masonry) {
          videoRef.current?.play().catch(console.error);
          onPlayingChange(true);
        }
      }}
      onMouseLeave={() => {
        if (masonry) {
          videoRef.current?.pause();
          onPlayingChange(false);
        }
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        className="h-full w-full object-contain relative z-1"
        ref={videoRef}
        aria-description={alt}
        poster={thumbnail}
        height={aspectRatio?.height}
        width={aspectRatio?.width}
      />
      {isSensitive && (
        <SensitiveOverlay
          variant="video"
          feedVariant={variant}
          actions={actions}
        />
      )}
      {!masonry && (
        <button
          type="button"
          className={cn(
            "z-10",
            "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-slatea-10 hover:bg-slate-12 text-slatedark-12 p-6 rounded-full",
            isPlaying ? "opacity-0 group-hover:opacity-100" : "opacity-100",
          )}
          onClick={togglePlaying}
        >
          {isPlaying ? (
            <PauseIcon className="h-8 w-8" />
          ) : (
            <PlayIcon className="h-8 w-8" />
          )}
        </button>
      )}
    </div>
  );
};

function PostEmbed({
  did,
  embed,
  isSensitive,
}: {
  did: string;
  embed: Embed;
  isSensitive: boolean;
}) {
  if (isImageEmbed(embed)) {
    return <PostImages images={embed.images} isSensitive={isSensitive} />;
  }

  if (isGalleryEmbed(embed)) {
    return (
      <PostImages images={galleryImages(did, embed)} isSensitive={isSensitive} />
    );
  }

  if (isVideoEmbed(embed)) {
    return (
      <div className="aspect-video h-[200px] bg-slate-3 dark:bg-slatedark-3 border border-slate-7 dark:border-slatedark-7 rounded-lg overflow-hidden relative">
        <VideoPlayer {...embed} isSensitive={isSensitive} />

        {embed.alt && (
          <AltBadge alt={embed.alt} className="absolute bottom-0 right-0 m-1" />
        )}
      </div>
    );
  }

  if (isExternalEmbed(embed)) {
    const isExternalImage =
      embed.external.thumb && embed.external.description.startsWith("ALT:");

    if (embed.external.thumb && isExternalImage) {
      return (
        <img
          src={embed.external.uri || makeImageUrl(did, embed.external.thumb)}
          className="w-full h-full object-contain bg-slate-3 dark:bg-slatedark-3 max-h-[400px]"
          alt={embed.external.title}
        />
      );
    }

    return (
      <a
        rel="noreferrer"
        target="_blank"
        href={embed.external.uri}
        className="relative border border-slate-normal rounded-lg overflow-hidden flex text-sm group"
      >
        {embed.external.thumb && (
          <div className="h-auto w-1/4 shrink-0 border-r border-slate-7 dark:border-slatedark-7">
            <img
              alt=""
              src={embed.external.thumb}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <div className="flex flex-col gap-1 px-3 py-2">
          <div className="font-semibold text-sm">
            {embed.external.title || embed.external.uri}
          </div>
          <div
            className="text-xs"
            // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
            dangerouslySetInnerHTML={{ __html: embed.external.description }}
          />
        </div>

        <div
          className={cn(
            "pointer-events-none absolute inset-0 flex items-center justify-center",
            "bg-slatea-2 dark:bg-slatedarka-2",
            "opacity-0 group-hover:opacity-100 transition-opacity",
          )}
        />
      </a>
    );
  }

  if (isMediaEmbed(embed)) {
    return (
      <PostEmbed did={did} embed={embed.media} isSensitive={isSensitive} />
    );
  }

  if (isStrongRefEmbed(embed)) {
    return null;
  }

  if (isFeedGenEmbed(embed)) {
    return (
      <a
        href={atUriToBskyUrl(embed.record.uri)}
        target="_blank"
        rel="noreferrer"
        className="bg-slate-ghost border rounded-md border-slate-normal p-1"
      >
        <div className="flex gap-2 items-center">
          <Avatar
            className="rounded-sm"
            src={embed.record.avatar}
            alt={embed.record.displayName}
          />
          <div className="flex flex-col gap-1">
            <div className="font-semibold">{embed.record.displayName}</div>
            <div className="text-xs text-slate-dim-overridable">
              @{embed.record.creator.handle}
            </div>
          </div>
        </div>
      </a>
    );
  }

  if (isRecordEmbed(embed)) {
    return (
      <div className="border-10 border-red-10">
        <PostText record={embed.record} />
      </div>
    );
  }

  // throw new Error("Unimplemented");
  return null;
}

export function PostText({
  record,
  className,
}: {
  record: Omit<Post, "id">;
  className?: string;
}) {
  const rt = new RichText(record.record);

  const text = [];

  for (const segment of rt.segments()) {
    if (segment.isLink()) {
      text.push(
        <Anchor
          key={segment.link?.uri}
          href={segment.link?.uri}
          target="_blank"
          rel="noreferrer"
        >
          {segment.text}
        </Anchor>,
      );
    } else if (segment.isMention()) {
      text.push(
        <Anchor
          key={segment.mention?.did}
          href={`https://bsky.app/profile/${segment.mention?.did}`}
          target="_blank"
          rel="noreferrer"
        >
          {segment.text}
        </Anchor>,
      );
    } else {
      text.push(segment.text);
    }
  }

  return (
    <div className={cn("break-words text-sm whitespace-pre-wrap", className)}>
      {text}
    </div>
  );
}

export function PreviewPostMenuItems({
  record,
  feedId,
}: {
  record: Post;
  feedId?: number;
}) {
  const hiddenPostIds = React.use(HiddenFeedItemsContext);
  const isHidden = hiddenPostIds[record.uri] ?? record.hidden;
  const removeHiddenFeedItem = React.use(RemoveHiddenFeedItemContext);
  const addHiddenFeedItem = React.use(AddHiddenFeedItemContext);
  const { toast } = useToast();
  // The same menu for owner and collaborator; only the door underneath differs, and each
  // item shows only if the server would let this person do it. See `lib/feedWrites.ts`.
  const access = useFeedAccessById(feedId ?? -1);
  const hidePostMutation = useMutation({
    onSuccess: () => toast({ title: "Removed from feed" }),
    onError: (e) =>
      toast({
        title: "Failed to hide post",
        description: apiErrorMessage(e),
      }),
    mutationFn: async () => {
      if (feedId) {
        addHiddenFeedItem(record.uri);
        try {
          await removePosts({
            feedId,
            postUrls: [record.uri],
            asCollaborator: access.isCollaborator,
          });
        } catch (e) {
          removeHiddenFeedItem(record.uri);
          throw e;
        }
      }
    },
  });
  const unHidePostMutation = useMutation({
    onSuccess: () => toast({ title: "Revealed post" }),
    onError: (e) =>
      toast({
        title: "Failed to reveal post",
        description: apiErrorMessage(e),
      }),
    mutationFn: async () => {
      if (feedId) {
        removeHiddenFeedItem(record.uri);
        try {
          await restorePost({
            feedId,
            postUri: record.uri,
            asCollaborator: access.isCollaborator,
          });
        } catch (e) {
          addHiddenFeedItem(record.uri);
          throw e;
        }
      }
    },
  });
  const hideAllMutation = useMutation({
    onSuccess: () => toast({ title: "Hide post across all feeds" }),
    onError: (e) =>
      toast({
        title: "Failed to hide post across all feeds",
        description: apiErrorMessage(e),
      }),
    mutationFn: () => {
      return hidePostForAllFeeds({ at_uri: record.uri });
    },
  });
  const onAddToFeedMutation = useMutation({
    retry: 2,
    onSuccess: () => {
      toast({ title: "Added to feed" });
    },
    mutationFn: (addToTop: boolean) => {
      if (!feedId) {
        throw new Error("Feed ID is required");
      }

      return insertPosts({
        feedId,
        postUrls: [record.uri],
        toTop: addToTop,
        asCollaborator: access.isCollaborator,
      });
    },
    onError: (e) =>
      toast({
        title: "Failed to add to feed",
        description: apiErrorMessage(e),
      }),
  });

  return (
    <>
      <MenuItem asChild={true}>
        <a
          className="flex items-center gap-1"
          href={atUriToBskyUrl(record.uri)}
          target="_blank"
          rel="noreferrer"
        >
          View on Bluesky
        </a>
      </MenuItem>
      {feedId && (
        <>
          <MenuSeparator />
          {/* Hides the post across every feed the VIEWER owns — meaningless on someone
              else's feed, so a collaborator is not offered it. */}
          {!access.isCollaborator && (
            <MenuItem onSelect={() => hideAllMutation.mutate()}>
              Remove from all feeds
            </MenuItem>
          )}
          {access.can("remove_posts") &&
            (isHidden ? (
              <MenuItem onSelect={() => unHidePostMutation.mutate()}>
                Unhide post
              </MenuItem>
            ) : (
              <MenuItem onSelect={() => hidePostMutation.mutate()}>
                Remove from feed
              </MenuItem>
            ))}
          <MenuSeparator />
          {access.can("add_posts_organic") && (
            <MenuItem onSelect={() => onAddToFeedMutation.mutate(false)}>
              Add to feed
            </MenuItem>
          )}
          {access.can("add_posts_promoted") && (
            <MenuItem onSelect={() => onAddToFeedMutation.mutate(true)}>
              Add to top of feed
            </MenuItem>
          )}
          <MenuSeparator />
        </>
      )}
    </>
  );
}

export function PreviewPostBadge({
  message,
  children,
}: {
  message: string;
  children: React.ReactNode;
}) {
  return (
    <SimpleTooltip message={message}>
      <div className="text-xs px-2 py-1 rounded-md  bg-slatea-action text-slate-normal">
        {children}
      </div>
    </SimpleTooltip>
  );
}

export const HideSensitiveContentContext = React.createContext<boolean>(false);
export const ImageStyleContext = React.createContext<boolean>(false);

export function PreviewPost({
  record,
  className,
  actions,
  isPresent,
  isDimmed,
  details,
  hideEngagement = false,
}: {
  record: Omit<Post, "id">;
  className?: string;
  actions?: React.ReactNode;
  isPresent?: boolean;
  isDimmed?: boolean;
  details?: React.ReactNode;
  hideEngagement?: boolean;
}) {
  const imageStyle = React.use(ImageStyleContext);
  const hiddenPostIds = React.use(HiddenFeedItemsContext);
  const isHidden = hiddenPostIds[record.uri] ?? record.hidden;
  const hiddenClass = (isHidden || isDimmed) && "opacity-50";
  const defaultDetails = (
    <>
      {isHidden && (
        <PreviewPostBadge message="Hidden on feed">
          Hidden <span className="sr-only">on feed</span>
        </PreviewPostBadge>
      )}
      {isPresent && !isHidden && (
        <SimpleTooltip message="Present on feed">
          <div className="text-xs p-1 rounded-md bg-slatea-ghost text-slate-normal">
            <CheckIcon /> <span className="sr-only">Present on feed</span>
          </div>
        </SimpleTooltip>
      )}
    </>
  );

  if (imageStyle) {
    return (
      <div className="relative">
        <div className={cn("flex flex-col", "bg-slate-app", className)}>
          {record.record.reply && (
            <div
              className={cn(
                hiddenClass,
                "text-xs text-reply-header-text bg-reply-header-bg text-center py-2 px-4 rounded-t",
              )}
            >
              <div className="flex items-center gap-1">
                <ReplyIcon className="h-3.5 w-3.5" />
                Reply
              </div>
            </div>
          )}
          <div className="flex flex-col pb-4 gap-3">
            {record.embed && (
              <PostEmbed
                did={record.author.did}
                embed={record.embed as Embed}
                isSensitive={isSensitiveByLabel(record)}
              />
            )}

            <div
              className={cn(
                hiddenClass,
                "flex gap-3 items-center px-4 text-slate-dim-overridable text-xs",
              )}
            >
              <div className="flex gap-1 items-center">
                <MessageCircleIcon className="h-3.5 w-3.5" />
                <div>{record.replyCount}</div>
              </div>
              <div className="flex gap-1 items-center">
                <Repeat2Icon className="h-3.5 w-3.5" />
                <div>{record.repostCount}</div>
              </div>
              <div className="flex gap-1 items-center">
                <HeartIcon className="h-3.5 w-3.5" />
                <div>{record.likeCount}</div>
              </div>
            </div>
            <div className="pl-4 pr-2 flex gap-3 items-center justify-between">
              <div
                className={cn(
                  hiddenClass,
                  "flex gap-3 items-center flex-1 min-w-0",
                )}
              >
                <Avatar
                  src={record.author.avatar}
                  alt={record.author.displayName || record.author.handle}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-normal font-medium truncate">
                    {record.author.displayName || record.author.handle}
                  </div>
                  <div className="text-xs text-slate-dim-overridable truncate">
                    @{record.author.handle}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <div
                  className={cn(
                    hiddenClass,
                    "text-xs text-slate-dim-overridable",
                    !actions && "px-2",
                  )}
                >
                  {record.record.createdAt &&
                    formatDistanceToNow(new Date(record.record.createdAt))}
                </div>
              </div>
            </div>
            <div className={cn(hiddenClass, "px-4 flex flex-col gap-3")}>
              <div className="p-1">
                <PostText record={record} />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-3 right-4 flex gap-2 items-center">
          {defaultDetails}
          {details}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        className={cn(
          "flex flex-col",
          "border border-slate-7 dark:border-slatedark-7",
          "rounded-lg overflow-hidden shadow-md",
          "bg-slate-app",
          className,
        )}
      >
        {record.record.reply && (
          <div
            className={cn(
              hiddenClass,
              "text-xs text-reply-header-text bg-reply-header-bg text-center py-2 px-4 rounded-t",
            )}
          >
            <div className="flex items-center gap-1">
              <ReplyIcon className="h-3.5 w-3.5" />
              Reply
            </div>
          </div>
        )}
        <div className="flex flex-col py-4 gap-3">
          <div className="pl-4 pr-2 flex gap-3 items-start justify-between">
            <div
              className={cn(
                hiddenClass,
                "flex gap-3 items-center flex-1 min-w-0",
              )}
            >
              <Avatar
                src={record.author.avatar}
                alt={record.author.displayName || record.author.handle}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-normal font-medium truncate">
                  {record.author.displayName || record.author.handle}
                </div>
                <div className="text-xs text-slate-dim-overridable truncate">
                  @{record.author.handle}
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div
                className={cn(
                  hiddenClass,
                  "text-xs text-slate-dim-overridable",
                  !actions && "px-2",
                )}
              >
                {record.record.createdAt &&
                  formatDistanceToNow(new Date(record.record.createdAt))}
              </div>
              {actions}
            </div>
          </div>
          <div className={cn(hiddenClass, "px-4 flex flex-col gap-3")}>
            <div className="p-1">
              <PostText record={record} />
            </div>
            {record.embed && (
              <PostEmbed
                did={record.author.did}
                embed={record.embed as Embed}
                isSensitive={isSensitiveByLabel(record)}
              />
            )}
          </div>

          {!hideEngagement && (
            <div
              className={cn(
                hiddenClass,
                "flex gap-3 items-center px-4 text-slate-dim-overridable text-xs",
              )}
            >
              <div className="flex gap-1 items-center">
                <MessageCircleIcon className="h-3.5 w-3.5" />
                <div>{record.replyCount}</div>
              </div>
              <div className="flex gap-1 items-center">
                <Repeat2Icon className="h-3.5 w-3.5" />
                <div>{record.repostCount}</div>
              </div>
              <div className="flex gap-1 items-center">
                <HeartIcon className="h-3.5 w-3.5" />
                <div>{record.likeCount}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute bottom-3 right-4 flex gap-2 items-center">
        {defaultDetails}
        {details}
      </div>
    </div>
  );
}

const MasonryContext = React.createContext<boolean>(false);

function VideoPreviewPost({
  record,
  className,
  actions,
  feedId,
  showPostActions,
}: {
  record: Post;
  className?: string;
  actions?: React.ReactNode;
  feedId?: number;
  showPostActions?: boolean;
}) {
  const masonry = React.use(MasonryContext);
  const hiddenPostIds = React.use(HiddenFeedItemsContext);
  const isHidden = hiddenPostIds[record.uri] ?? record.hidden;
  const hiddenClass = isHidden && "opacity-35";
  const [isPlaying, setIsPlaying] = React.useState(false);
  const isSensitive = isSensitiveByLabel(record);

  if (!record.embed?.$type.includes("app.bsky.embed.video")) {
    return null;
  }

  const video = record.embed as unknown as {
    thumbnail: string;
    playlist: string;
    alt: string;
    aspectRatio: {
      height: number;
      width: number;
    };
  };

  return (
    <div
      className={cn("grid bg-slatedark-2 group relative", className)}
      key={record.cid}
    >
      <VideoPlayer
        {...video}
        style={{
          gridRow: "1",
          gridColumn: "1",
          minHeight: masonry ? undefined : 400,
          margin: "auto",
        }}
        className={cn(hiddenClass)}
        onPlayingChange={setIsPlaying}
        isSensitive={isSensitive}
        variant="video"
        actions={
          showPostActions && (
            <DropdownMenu>
              <MenuTrigger asChild>
                <Button variant="action" size="smIcon">
                  <span className="sr-only">More actions</span>
                  <DotsVerticalIcon />
                </Button>
              </MenuTrigger>
              <MenuContent>
                <PreviewPostMenuItems record={record} feedId={feedId} />
              </MenuContent>
            </DropdownMenu>
          )
        }
      />
      {!isPlaying && (
        <div
          className={cn(
            "relative z-2 flex flex-col gap-3 py-4 bg-linear-to-t from-slatea-12 via-slatea-10 via-60% h-fit self-end text-slatedark-12",
            !masonry && "pt-14",
            "pointer-events-none",
          )}
          style={{ gridRow: "1", gridColumn: "1" }}
        >
          <div className="pl-4 pr-2 flex gap-3 items-start justify-between">
            <div className={cn(hiddenClass, "flex gap-3 items-center")}>
              <Avatar
                src={record.author.avatar}
                alt={record.author.displayName || record.author.handle}
              />
              <div>
                <div className="text-sm">
                  {record.author.displayName || record.author.handle}
                </div>
                <div className="text-xs text-slatedark-11">
                  @{record.author.handle}
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div className={cn(hiddenClass, "text-xs text-slatedark-11")}>
                {record.record.createdAt &&
                  formatDistanceToNow(new Date(record.record.createdAt))}
              </div>
              {actions}
            </div>
          </div>
          <div className={cn(hiddenClass, "px-4 inline-flex gap-3")}>
            <PostText record={record} className="line-clamp-3" />
          </div>
          <div
            className={cn(
              hiddenClass,
              "flex gap-3 items-center px-4 text-slatedark-11 text-sm",
            )}
          >
            <div className="flex gap-1 items-center">
              <MessageCircleIcon className="h-3.5 w-3.5" />
              <div>{record.replyCount}</div>
            </div>
            <div className="flex gap-1 items-center">
              <Repeat2Icon className="h-3.5 w-3.5" />
              <div>{record.repostCount}</div>
            </div>
            <div className="flex gap-1 items-center">
              <HeartIcon className="h-3.5 w-3.5" />
              <div>{record.likeCount}</div>
            </div>
            {record.record?.embed?.alt && (
              <AltBadge alt={record.record.embed.alt} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const HiddenFeedItemsContext = React.createContext<Record<string, boolean>>({});
const RemoveHiddenFeedItemContext = React.createContext<(uri: string) => void>(
  () => {
    throw new Error("RemoveHiddenFeedItemContext not provided");
  },
);
const AddHiddenFeedItemContext = React.createContext<(uri: string) => void>(
  () => {
    throw new Error("AddHiddenFeedItemContext not provided");
  },
);

function LoaderRow({
  isFetchingNextPage,
  fetchNextPage,
}: {
  isFetchingNextPage: boolean;
  fetchNextPage?: () => Promise<unknown>;
}) {
  const loaderRef = React.useRef<HTMLDivElement>(null);
  const isFetchingRef = React.useRef(isFetchingNextPage);

  React.useEffect(() => {
    isFetchingRef.current = isFetchingNextPage;
  }, [isFetchingNextPage]);

  React.useEffect(() => {
    if (!loaderRef.current || !fetchNextPage) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting && !isFetchingRef.current) {
        fetchNextPage().catch(console.error);
      }
    });

    observer.observe(loaderRef.current);

    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage]);

  return (
    <div
      className="flex items-center justify-center flex-1 p-4"
      ref={loaderRef}
    >
      <Spinner />
    </div>
  );
}

export function HiddenPostProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [hiddenFeedItems, setHiddenFeedItems] = React.useState<
    Record<string, boolean>
  >({});
  const addHiddenFeedItem = React.useCallback((uri: string) => {
    setHiddenFeedItems((prev) => {
      return {
        ...prev,
        [uri]: true,
      };
    });
  }, []);
  const removeHiddenFeedItem = React.useCallback((uri: string) => {
    setHiddenFeedItems((prev) => {
      return {
        ...prev,
        [uri]: false,
      };
    });
  }, []);

  return (
    <HiddenFeedItemsContext value={hiddenFeedItems}>
      <RemoveHiddenFeedItemContext value={removeHiddenFeedItem}>
        <AddHiddenFeedItemContext value={addHiddenFeedItem}>
          {children}
        </AddHiddenFeedItemContext>
      </RemoveHiddenFeedItemContext>
    </HiddenFeedItemsContext>
  );
}

export function PreviewFeedList({
  feedId,
  posts,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  warning,
  isLoading,
  error,
  onDebugPost,
  isVideo,
  virtualization = "scroll",
  showPostActions = true,
  frameless,
  className,
}: {
  feedId: number | undefined;
  posts: Post[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage?: () => Promise<unknown>;
  onDebugPost?: (post: Post) => void;
  warning?: React.ReactNode;
  isLoading: boolean;
  error?: Error | null;
  isVideo: boolean;
  virtualization?: "window" | "scroll";
  showPostActions?: boolean;
  frameless?: boolean;
  className?: string;
}) {
  "use no memo";

  const parentRef = React.useRef<HTMLDivElement>(null);
  const areaRef = React.useRef<HTMLDivElement>(null!);
  const size = useSize(areaRef);

  React.useEffect(() => {
    if (!window.frameElement || !size) return;

    window.parent.postMessage(
      ["setHeight", { height: size.height, id: window.frameElement.id }],
      "*",
    );
  }, [size]);

  // Dont change that prop!
  const virtualizer =
    virtualization === "window"
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useWindowVirtualizer({
          count: hasNextPage ? posts.length + 1 : posts.length,
          estimateSize: () => 250,
          overscan: 5,
        })
      : // eslint-disable-next-line react-hooks/rules-of-hooks
        useVirtualizer({
          count: hasNextPage ? posts.length + 1 : posts.length,
          getScrollElement: () => parentRef.current,
          estimateSize: () => 250,
          overscan: 5,
        });
  const virtualItems = virtualizer.getVirtualItems();
  const imageStyle = React.use(ImageStyleContext);

  const list = (
    <div
      className="w-full relative"
      style={{ height: virtualizer.getTotalSize() }}
    >
      <div
        className={cn(
          "w-full absolute top-0 left-0 flex flex-col",
          !frameless && "bg-slate-3 dark:bg-slatedark-3",
          !isVideo && !imageStyle && "gap-4 p-4",
        )}
        style={{
          transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
        }}
      >
        {virtualItems.map((virtualRow) => {
          const isLoaderRow = virtualRow.index > posts.length - 1;
          const post = posts[virtualRow.index];
          const PostComponent = isVideo ? VideoPreviewPost : PreviewPost;

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
            >
              {isLoaderRow ? (
                hasNextPage ? (
                  <LoaderRow
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                  />
                ) : (
                  <div className="flex items-center justify-center flex-1 p-4">
                    <span className="text-sm text-slate-dim-overridable">
                      End of feed
                    </span>
                  </div>
                )
              ) : (
                post && (
                  <PostComponent
                    record={post}
                    feedId={feedId}
                    showPostActions={showPostActions}
                    className={isVideo ? "border-b border-slatedark-7" : ""}
                    actions={
                      showPostActions && (
                        <DropdownMenu>
                          <MenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="smIcon"
                              className={cn(
                                isVideo &&
                                  "text-slatedark-12 bg-slatedarka-5 hover:bg-slatedarka-6 !aria-[expanded=true]:bg-slatedarka-6 pointer-events-auto",
                              )}
                            >
                              <span className="sr-only">More actions</span>
                              <DotsVerticalIcon />
                            </Button>
                          </MenuTrigger>
                          <MenuContent align="end">
                            <CopyUserDidMenuItem post={post} />
                            <CopyLinkMenuItem post={post} />
                            <MenuSeparator />
                            {onDebugPost && (
                              <MenuItem onSelect={() => onDebugPost(post)}>
                                Debug post
                              </MenuItem>
                            )}
                            <PdsLsMenuItem post={post} />
                            {post.sticky_id && feedId ? (
                              <StickyPostMenuItems
                                isActive={true}
                                feedId={feedId}
                                stickyPostId={post.sticky_id}
                                postUri={post.uri}
                              />
                            ) : (
                              <PreviewPostMenuItems
                                record={post}
                                feedId={feedId}
                              />
                            )}
                          </MenuContent>
                        </DropdownMenu>
                      )
                    }
                  />
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <HiddenPostProvider>
      <div
        ref={areaRef}
        className={cn(
          "flex flex-col overflow-auto relative w-full h-full",
          className,
        )}
      >
        {error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center gap-2 p-4 text-red-10 dark:text-reddark-10">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <div className="text-sm">{error.message}</div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="h-full w-full p-10 flex items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <>
            {warning && (
              <div className="flex items-center gap-3 bg-amber-4 dark:bg-amberdark-4 text-amber-dark dark:text-amberdark-dark text-xs p-4">
                <ExclamationTriangleIcon className="shrink-0 h-5 w-5" />
                <span className="text-xs md:text-sm flex-1">{warning}</span>
              </div>
            )}

            {virtualization === "scroll" ? (
              <div
                ref={parentRef}
                className="overflow-auto contain-strict"
                id="preview-feed"
                style={{
                  height: size?.height ?? "100%",
                  width: size?.width ?? "100%",
                }}
              >
                {list}
              </div>
            ) : (
              <div id="preview-feed">{list}</div>
            )}
          </>
        )}
      </div>
    </HiddenPostProvider>
  );
}

export function MasonryPreviewFeedList({
  posts,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  isVideo,
  mode = "click",
  frameless,
}: {
  posts: Post[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage?: () => Promise<unknown>;
  isLoading: boolean;
  isVideo: boolean;
  mode?: "infinite" | "click";
  frameless?: boolean;
}) {
  "use no memo";

  const imageStyle = React.use(ImageStyleContext);
  const areaRef = React.useRef<HTMLDivElement>(null!);
  const size = useSize(areaRef);

  React.useEffect(() => {
    if (!window.frameElement || !size) return;

    window.parent.postMessage(
      ["setHeight", { height: size.height, id: window.frameElement.id }],
      "*",
    );
  }, [size]);

  const gridRef = React.useRef<HTMLDivElement>(null);
  const layoutRef = React.useRef<InstanceType<typeof Masonry> | null>(null);

  React.useLayoutEffect(() => {
    function onLoad() {
      layoutRef.current?.layout?.();
    }

    const element = gridRef.current;

    if (!element) return;

    import("masonry-layout")
      .then(({ default: Masonry }) => {
        layoutRef.current = new Masonry(element, {
          // options
          itemSelector: ".grid-item",
          columnWidth: ".grid-size",
          gutter: imageStyle ? 4 : 8,
        });

        element.addEventListener("load", onLoad, {
          capture: true,
        });
      })
      .catch(console.error);

    return () => {
      element.removeEventListener("load", onLoad, {
        capture: true,
      });
    };
  }, [imageStyle, posts.length]);

  React.useLayoutEffect(() => {
    if (!gridRef.current || !layoutRef.current) return;

    const items = [];
    const currentItems = layoutRef.current.getItemElements?.();

    for (const post of posts) {
      const item = document.getElementById(post.cid.toString());

      if (item && !currentItems?.includes(item)) {
        items.push(item);
      }
    }

    layoutRef.current.addItems?.(items);
  }, [posts.length, posts]);

  const list = (
    <div
      className={cn(
        "w-full relative",
        !frameless && "bg-slate-3 dark:bg-slatedark-3",
      )}
    >
      <div ref={gridRef}>
        <div className="grid-size w-full sm:w-[calc((100%-8px)*(1/2))] md:w-[calc((100%-16px)*(1/3))]" />
        {posts.map((post, index) => {
          const isLoaderRow = index > posts.length - 1;
          const PostComponent = isVideo ? VideoPreviewPost : PreviewPost;

          return (
            <div
              key={post.cid}
              id={post.cid?.toString()}
              className={cn(
                "grid-item w-full sm:w-[calc((100%-8px)*(1/2))] md:w-[calc((100%-16px)*(1/3))]",
                imageStyle ? "mt-1" : "mt-2",
              )}
            >
              {isLoaderRow ? (
                hasNextPage ? (
                  <LoaderRow
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                  />
                ) : (
                  <div className="flex items-center justify-center flex-1 p-4">
                    <span className="text-sm text-slate-dim-overridable">
                      End of feed
                    </span>
                  </div>
                )
              ) : (
                post && <PostComponent record={post} />
              )}
            </div>
          );
        })}
      </div>

      {hasNextPage &&
        fetchNextPage &&
        (mode === "infinite" ? (
          <LoadMoreSpinner
            fetchNextPage={fetchNextPage}
            isLoading={isLoading}
          />
        ) : (
          <div className="flex items-center justify-center p-8">
            <Button
              onPress={() => {
                fetchNextPage().catch(console.error);
              }}
              isLoading={isFetchingNextPage}
            >
              Load more
            </Button>
          </div>
        ))}
    </div>
  );

  return (
    <MasonryContext value={true}>
      <HiddenPostProvider>
        <div
          ref={areaRef}
          className="flex flex-col overflow-auto relative w-full h-full"
        >
          {isLoading ? (
            <div className="h-full w-full p-10 flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div id="preview-feed">{list}</div>
          )}
        </div>
      </HiddenPostProvider>
    </MasonryContext>
  );
}

export function CarouselPreviewFeedList({
  posts,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isLoading,
  isVideo,
  mode = "scroll",
  frameless,
}: {
  posts: Post[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage?: () => Promise<unknown>;
  isLoading: boolean;
  isVideo: boolean;
  mode?: "auto" | "scroll";
  frameless?: boolean;
}) {
  "use no memo";

  const areaRef = React.useRef<HTMLDivElement>(null!);
  const size = useSize(areaRef);
  const scrollRef = React.useRef<HTMLDivElement>(null!);

  React.useEffect(() => {
    if (!window.frameElement || !size) return;

    window.parent.postMessage(
      ["setHeight", { height: size.height, id: window.frameElement.id }],
      "*",
    );
  }, [size]);

  useEffect(() => {
    if (mode === "auto") {
      const interval = setInterval(() => {
        scrollRef.current?.scrollTo({
          left: scrollRef.current?.scrollLeft + 300,
          behavior: "smooth",
        });
      }, 7500);

      return () => clearInterval(interval);
    }
  }, [mode]);

  const list = (
    <div
      ref={scrollRef}
      className={cn(
        "w-full relative",
        "flex items-center gap-2 px-8",
        !frameless && "bg-slate-3 dark:bg-slatedark-3",
      )}
      style={{
        scrollSnapType: "x mandatory",
        overflowX: "auto",
        display: "flex",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "smooth",
      }}
    >
      {posts.map((post, index) => {
        const isLoaderRow = index > posts.length - 1;
        const PostComponent = isVideo ? VideoPreviewPost : PreviewPost;

        return (
          <div
            key={post.id}
            style={{
              flex: "0 0 min(32%, 420px)",
              scrollSnapAlign: "center",
            }}
          >
            {isLoaderRow ? (
              hasNextPage ? (
                <LoaderRow
                  isFetchingNextPage={isFetchingNextPage}
                  fetchNextPage={fetchNextPage}
                />
              ) : (
                <div className="flex items-center justify-center flex-1 p-4">
                  <span className="text-sm text-slate-dim-overridable">
                    End of feed
                  </span>
                </div>
              )
            ) : (
              post && <PostComponent record={post} />
            )}
          </div>
        );
      })}

      {hasNextPage && fetchNextPage && (
        <LoadMoreSpinner fetchNextPage={fetchNextPage} isLoading={isLoading} />
      )}
    </div>
  );

  return (
    <MasonryContext value={true}>
      <HiddenPostProvider>
        <div
          ref={areaRef}
          className="flex flex-col overflow-auto relative w-full h-full"
        >
          {isLoading ? (
            <div className="h-full w-full p-10 flex items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <div id="preview-feed">{list}</div>
          )}
        </div>
      </HiddenPostProvider>
    </MasonryContext>
  );
}

export function PublishedPreviewFeed({
  feedId,
  onDebugPost,
  virtualization = "scroll",
  showPostActions = true,
}: {
  feedId: number;
  onDebugPost?: (post: Post) => void;
  virtualization?: "window" | "scroll";
  showPostActions?: boolean;
}) {
  "use no memo";

  const {
    data: preview,
    error,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery(getCurrentFeedOptions(feedId));
  const feed = useSuspenseQuery(feedQueryOptions(feedId));
  // eslint-disable-next-line @eslint-react/naming-convention/use-state
  const [now] = React.useState(() => Date.now());
  const wasUpdatedInTheLastHour =
    now - new Date(feed.data?.updated_at).getTime() < 3600000;

  const allPosts = React.useMemo(
    () => (preview ? preview.pages.flatMap((d) => d.posts) : []),
    [preview],
  );

  const refetchRef = React.useRef(refetch);
  React.useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  React.useEffect(() => {
    if (allPosts.length > 0) return;

    const interval = setInterval(() => {
      refetchRef.current().catch(console.error);
    }, 60_000);

    return () => clearInterval(interval);
  }, [allPosts.length]);

  return (
    <PreviewFeedList
      feedId={feedId}
      onDebugPost={onDebugPost}
      posts={allPosts}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      warning={
        wasUpdatedInTheLastHour &&
        "This feed was updated in the last hour. The feed will contain posts from before your algorithm was updated. This will resolve as new posts are added to the feed."
      }
      isLoading={isLoading}
      error={error}
      isVideo={isVideoFeed(feed.data.draft_algorithm_manifest)}
      virtualization={virtualization}
      showPostActions={showPostActions}
    />
  );
}

export function WorkbenchPreviewFeedBase({
  feedVersion,
  feedId,
  onDebugPost,
  autoStart = true,
}: {
  feedId?: number;
  feedVersion: FeedVersion;
  onDebugPost?: (post: Post) => void;
  autoStart?: boolean;
}) {
  const actor = CanvasEditorMachine.useActorRef();
  const message = CanvasEditorMachine.useSelector(
    (s) => s.context.generateMessage,
  );
  const isGenerating = CanvasEditorMachine.useSelector((s) =>
    s.matches("idle.generateFeed.running"),
  );
  const posts = CanvasEditorMachine.useSelector((s) => s.context.generatedFeed);
  const onRegenerate = useEvent(() => {
    actor.send({
      type: "REGENERATE_FEED",
      feedVersion,
    });
  });
  const onGenerate = useEvent(() => {
    actor.send({
      type: "GENERATE_FEED",
      feedVersion,
    });
  });

  React.useEffect(() => {
    if (!feedVersion || !autoStart) return;

    actor.send({
      type: "GENERATE_FEED",
      feedVersion,
    });
  }, [actor, feedVersion, autoStart]);

  const regenerateButton = (
    <Button size="xs" variant="ghost" onPress={onRegenerate}>
      Regenerate
    </Button>
  );

  if (!posts.length) {
    if (!autoStart && !isGenerating) {
      return (
        <div className="flex flex-col items-center gap-4 h-full justify-center">
          <div className="text-slate-dim text-sm">
            Start creating a preview for the edits to your feed.
          </div>
          <Button size="xs" onPress={onGenerate}>
            Generate!
          </Button>
        </div>
      );
    }

    if (!isGenerating) {
      return (
        <div className="flex flex-col items-center gap-4 h-full justify-center">
          <div className="text-slate-dim text-sm">
            You need to fill out all fields to generate a feed.
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center gap-4 h-full justify-center">
        <Spinner />
        <div className="text-slate-dim text-sm">{message || "Starting..."}</div>
        {regenerateButton}
      </div>
    );
  }

  return (
    <PreviewFeedList
      feedId={feedId}
      posts={posts}
      hasNextPage={true}
      isLoading={false}
      isFetchingNextPage={true}
      error={null}
      isVideo={isVideoFeed(feedVersion)}
      warning={
        <div className="flex gap-4 items-center">
          <div className="flex-1">{message}</div>
          {regenerateButton}
        </div>
      }
      onDebugPost={onDebugPost}
    />
  );
}

export function WorkbenchPreviewFeed({
  feedVersion,
  feedId,
}: {
  feedId?: number;
  feedVersion: FeedVersion;
}) {
  const editor = useEditor();
  const actor = CanvasEditorMachine.useActorRef();

  return (
    <WorkbenchPreviewFeedBase
      feedVersion={feedVersion}
      feedId={feedId}
      onDebugPost={(post: Post) => {
        actor.send({
          type: "START_AUDIT",
          urls: [atUriToBskyUrl(post.uri)],
          manifest: feedVersion.manifest,
          editor,
          algo_id: feedId,
        });
      }}
    />
  );
}

export interface PreviewFeedProps {
  feedId: number;
  workbench?: boolean;
  feedVersion: FeedVersion;
  onDebugPost?: (post: Post) => void;
}

export function PreviewFeed({
  feedId,
  workbench,
  feedVersion,
  onDebugPost,
}: PreviewFeedProps) {
  if (workbench) {
    return <WorkbenchPreviewFeed feedId={feedId} feedVersion={feedVersion} />;
  }

  return <PublishedPreviewFeed feedId={feedId} onDebugPost={onDebugPost} />;
}
